import { useQuery, useQueryClient } from "@tanstack/react-query";
import { transcriptKeys } from "./queryKeys";
import { getTranscript, listTranscripts, listTranscriptsForCandidate } from "./api";
import type { Transcript, TranscriptListItem } from "@/types/transcript";

/** All transcripts of the caller's organization, without transcript bodies. */
export function useTranscripts() {
	return useQuery({
		queryKey: transcriptKeys.list(),
		queryFn: listTranscripts,
		staleTime: 60_000,
	});
}

/**
 * Full transcript detail. Seeds placeholder data from the list cache so the
 * header and metadata render instantly while the body loads.
 */
export function useTranscript(transcriptId: string | null) {
	const queryClient = useQueryClient();

	return useQuery<Transcript>({
		queryKey: transcriptKeys.detail(transcriptId || "none"),
		queryFn: () => getTranscript(transcriptId!),
		enabled: !!transcriptId,
		staleTime: 5 * 60_000,
		placeholderData: () => {
			const listItem = queryClient
				.getQueryData<TranscriptListItem[]>(transcriptKeys.list())
				?.find((t) => t.id === transcriptId);
			if (!listItem)
				return undefined;
			return { ...listItem, transcript_content: null };
		},
	});
}

/** Transcripts linked to one candidate, for the embedded candidate tab. */
export function useCandidateTranscripts(candidateId: string) {
	return useQuery({
		queryKey: transcriptKeys.forCandidate(candidateId),
		queryFn: () => listTranscriptsForCandidate(candidateId),
		enabled: !!candidateId,
		staleTime: 60_000,
	});
}
